import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CreditCard, Lock, ShieldCheck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // { code, discount }
  const [couponLoading, setCouponLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const finalTotal = coupon ? Math.max(0, total - coupon.discount) : total;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const result = await api.validateCoupon(couponInput, total);
      setCoupon(result);
      toast.success(`Cupón ${result.code} aplicado: -${result.discount.toFixed(2).replace('.', ',')} €`);
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.detail || 'Cupón no válido');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponInput(''); };

  if (items.length === 0 && !processing) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-stone-500 mb-4">No hay productos en tu carrito.</p>
        <Link to="/tienda" className="text-teal-600 font-bold">Ir a la tienda</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Completa todos los campos para continuar');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error('Introduce un correo electrónico válido');
      return;
    }
    setProcessing(true);
    try {
      // Stripe pendiente de integrar: el pedido se registra vía API (modo demo)
      const order = await api.createOrder({
        customer: form,
        items: items.map((i) => ({ productId: i.productId, name: i.name, qty: i.qty, price: i.price })),
        couponCode: coupon?.code || null,
      });
      clearCart();
      navigate(`/confirmacion/${order.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo procesar el pedido');
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-black text-stone-800 mb-2" data-testid="checkout-title">Finalizar compra</h1>
      <p className="text-stone-500 mb-8">Compra como invitado. Sin registro, sin complicaciones.</p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 space-y-5" data-testid="checkout-form">
          <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4">
            <h2 className="font-black text-stone-800">Tus datos</h2>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Nombre completo *</label>
              <input value={form.name} onChange={set('name')} className="w-full border border-stone-300 px-4 py-2.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="Tu nombre y apellidos" data-testid="checkout-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Correo electrónico *</label>
              <input value={form.email} onChange={set('email')} type="email" className="w-full border border-stone-300 px-4 py-2.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="tucorreo@email.com" data-testid="checkout-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Teléfono *</label>
              <input value={form.phone} onChange={set('phone')} className="w-full border border-stone-300 px-4 py-2.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="600 000 000" data-testid="checkout-phone" />
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-lg p-6">
            <h2 className="font-black text-stone-800 mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5 text-stone-600" /> Pago</h2>
            <div className="bg-stone-50 border border-dashed border-stone-300 rounded p-4 text-sm text-stone-500 flex items-start gap-2">
              <Lock className="w-4 h-4 mt-0.5 shrink-0 text-stone-400" />
              <span>El pago seguro con <strong>Stripe</strong> se activará próximamente. En esta fase la compra se registra en modo demostración.</span>
            </div>
          </div>

          <button type="submit" disabled={processing} className="btn-natur w-full py-3.5 rounded font-bold text-sm disabled:opacity-60" data-testid="place-order">
            {processing ? 'Procesando pedido…' : `Realizar pedido · ${finalTotal.toFixed(2).replace('.', ',')} €`}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-stone-400"><ShieldCheck className="w-3.5 h-3.5" /> Compra protegida. Activación inmediata tras confirmación. Al comprar aceptas las <Link to="/legal/condiciones-generales" className="underline">Condiciones Generales</Link>.</p>
        </form>

        <div className="lg:col-span-2">
          <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="checkout-summary">
            <h2 className="font-black text-stone-800 mb-4">Tu pedido</h2>
            <div className="space-y-3 mb-4">
              {items.map((i) => (
                <div key={i.productId} className="flex items-center gap-3">
                  <img src={i.image} alt={i.name} className="w-12 h-12 object-cover rounded" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-stone-800">{i.name}</p>
                    <p className="text-xs text-stone-500">x{i.qty}</p>
                  </div>
                  <span className="text-sm font-bold text-stone-800">{(i.price * i.qty).toFixed(2).replace('.', ',')} €</span>
                </div>
              ))}
            </div>
            {/* Cupón promocional */}
            <div className="border-t border-stone-200 pt-4 mb-4">
              {coupon ? (
                <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded px-3 py-2.5" data-testid="coupon-applied">
                  <span className="text-sm font-bold text-teal-700">{coupon.code}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-teal-700">-{coupon.discount.toFixed(2).replace('.', ',')} €</span>
                    <button type="button" onClick={removeCoupon} className="text-xs text-stone-400 hover:text-red-500 transition-colors underline" data-testid="remove-coupon">Quitar</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="Cupón promocional"
                    className="flex-1 border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="coupon-input"
                  />
                  <button type="button" onClick={applyCoupon} disabled={couponLoading} className="bg-stone-800 hover:bg-stone-700 transition-colors text-white px-4 py-2 rounded text-xs font-bold disabled:opacity-60" data-testid="apply-coupon">
                    {couponLoading ? '…' : 'Aplicar'}
                  </button>
                </div>
              )}
            </div>
            {coupon && (
              <div className="flex justify-between text-sm text-stone-500 mb-2">
                <span>Subtotal</span><span>{total.toFixed(2).replace('.', ',')} €</span>
              </div>
            )}
            {coupon && (
              <div className="flex justify-between text-sm text-teal-600 font-bold mb-2" data-testid="discount-line">
                <span>Descuento</span><span>-{coupon.discount.toFixed(2).replace('.', ',')} €</span>
              </div>
            )}
            <div className="border-t border-stone-200 pt-4 flex justify-between font-black text-stone-800">
              <span>Total</span><span data-testid="checkout-total">{finalTotal.toFixed(2).replace('.', ',')} €</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
